import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";

export const DEFAULT_SIDEBAR_WIDTH = 248;
export const MIN_SIDEBAR_WIDTH = 240;
export const MAX_SIDEBAR_WIDTH = 400;
const STORAGE_KEY = "padpulse:sidebar:width";

function clampWidth(width: number) {
  return Math.round(Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, width)));
}

function readWidth() {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    const width = saved ? Number(saved) : NaN;
    return Number.isFinite(width) ? clampWidth(width) : DEFAULT_SIDEBAR_WIDTH;
  } catch {
    return DEFAULT_SIDEBAR_WIDTH;
  }
}

/** Browser-local preference survives the per-page AppLayout remounts. */
export function useSidebarWidth() {
  const [width, setWidth] = useState(readWidth);
  const [isResizing, setIsResizing] = useState(false);
  const drag = useRef<{ pointerId: number; startX: number; startWidth: number } | null>(null);

  // Persist at the end of a drag, not on every pointer movement.
  useEffect(() => {
    if (isResizing) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, String(width));
    } catch {
      // Optional preferences must not break navigation in storage-restricted browsers.
    }
  }, [width, isResizing]);

  const finishDrag = (element: HTMLElement, cancel = false) => {
    const current = drag.current;
    if (!current) return;
    drag.current = null;
    if (cancel) setWidth(current.startWidth);
    setIsResizing(false);
    if (element.hasPointerCapture?.(current.pointerId)) {
      element.releasePointerCapture(current.pointerId);
    }
  };

  const handleProps = {
    role: "separator" as const,
    tabIndex: 0,
    "aria-label": "Resize navigation sidebar",
    "aria-orientation": "vertical" as const,
    "aria-controls": "app-sidebar",
    "aria-valuemin": MIN_SIDEBAR_WIDTH,
    "aria-valuemax": MAX_SIDEBAR_WIDTH,
    "aria-valuenow": width,
    "aria-valuetext": `${width} pixels`,
    title: "Drag to resize. Use arrow keys to adjust. Double-click to reset.",
    onPointerDown: (event: PointerEvent<HTMLElement>) => {
      if (event.button !== 0) return;
      event.preventDefault();
      event.currentTarget.focus();
      event.currentTarget.setPointerCapture(event.pointerId);
      drag.current = { pointerId: event.pointerId, startX: event.clientX, startWidth: width };
      setIsResizing(true);
    },
    onPointerMove: (event: PointerEvent<HTMLElement>) => {
      const current = drag.current;
      if (!current || current.pointerId !== event.pointerId) return;
      setWidth(clampWidth(current.startWidth + event.clientX - current.startX));
    },
    onPointerUp: (event: PointerEvent<HTMLElement>) => {
      if (drag.current?.pointerId === event.pointerId) finishDrag(event.currentTarget);
    },
    onPointerCancel: (event: PointerEvent<HTMLElement>) => {
      if (drag.current?.pointerId === event.pointerId) finishDrag(event.currentTarget, true);
    },
    onLostPointerCapture: (event: PointerEvent<HTMLElement>) => {
      if (drag.current?.pointerId === event.pointerId) finishDrag(event.currentTarget);
    },
    onDoubleClick: () => setWidth(DEFAULT_SIDEBAR_WIDTH),
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
      if (event.key === "Escape" && drag.current) {
        event.preventDefault();
        finishDrag(event.currentTarget, true);
        return;
      }
      const step = event.shiftKey ? 24 : 8;
      const next = event.key === "ArrowLeft" ? width - step
        : event.key === "ArrowRight" ? width + step
        : event.key === "Home" ? MIN_SIDEBAR_WIDTH
        : event.key === "End" ? MAX_SIDEBAR_WIDTH
        : event.key === "Enter" ? DEFAULT_SIDEBAR_WIDTH
        : null;
      if (next === null) return;
      event.preventDefault();
      setWidth(clampWidth(next));
    },
  };

  return { width, isResizing, handleProps };
}